import { createSelector } from 'reselect';

import { TeamChatConnectionStatus } from 'constants/teamChat';

const EMPTY_MESSAGES = [];
const EMPTY_CHANNELS_LIST = [];
const EMPTY_TYPING = {};
const EMPTY_ONLINE_USERS = [];
const EMPTY_MENTIONS = [];
const EMPTY_READ_RECEIPTS = {};

const selectTeamChat = state => state.teamChat;

const selectTeamChatChannels = state => state.teamChat.channels;

const getChannelTypeFromId = channelId => {
  if (!channelId) {
    return null;
  }

  const index = channelId.lastIndexOf('_');

  if (index === -1) {
    return null;
  }

  return channelId.slice(index + 1).toLowerCase();
};

const selectTeamChatConnectionStatus = createSelector(
  selectTeamChat,
  ({ connectionStatus }) => connectionStatus,
);

export const selectIsTeamChatConnected = createSelector(
  selectTeamChatConnectionStatus,
  status => status === TeamChatConnectionStatus.CONNECTED,
);

export const selectTeamChatIsLoadingChannels = createSelector(
  selectTeamChat,
  ({ isLoadingChannels }) => isLoadingChannels,
);

const selectTeamChatChannelsByTypeMemoized = createSelector(
  [selectTeamChatChannels, (_state, channelType) => channelType],
  (channels, channelType) => {
    const normalizedType = channelType?.toLowerCase() ?? null;

    const list = Object.entries(channels)
      .filter(([id]) => getChannelTypeFromId(id) === normalizedType)
      .map(([, channel]) => channel);

    return list.length ? list : EMPTY_CHANNELS_LIST;
  },
);

export const selectTeamChatChannelsByType = channelType => state =>
  selectTeamChatChannelsByTypeMemoized(state, channelType);

export const selectTeamChatAllChannels = createSelector(selectTeamChatChannels, channels => {
  const list = Object.values(channels);

  return list.length ? list : EMPTY_CHANNELS_LIST;
});

export const selectTeamChatTotalUnreadCount = createSelector(selectTeamChatChannels, channels =>
  Object.values(channels).reduce((total, channel) => total + (channel?.unreadMessageCount ?? 0), 0),
);

export const selectTeamChatChannel = chatId => state =>
  chatId ? (state.teamChat.channels[chatId] ?? null) : null;

export const selectTeamChatMessages = chatId => state =>
  chatId ? (state.teamChat.messages[chatId] ?? EMPTY_MESSAGES) : EMPTY_MESSAGES;

export const selectTeamChatIsLoadingMessages = chatId => state =>
  !!state.teamChat.isLoadingMessages[chatId];

export const selectTeamChatIsSendingMessage = chatId => state => !!state.teamChat.isSending[chatId];

export const selectTeamChatHasMoreMessages = chatId => state => !!state.teamChat.hasMore[chatId];

// `hasMore[chatId]` is only ever set (to a real boolean) inside FETCH_MESSAGES_SUCCESS, so its mere
// presence — regardless of value — distinguishes "fetched at least once (even zero messages)" from
// "never fetched", which plain `hasMore` truthiness can't (both a never-loaded and a
// loaded-with-no-more channel read as falsy). Powers the DEV-5448 entity-reference badges/rail,
// which need the channel's messages loaded even when the loan chat drawer was never opened.
export const selectTeamChatMessagesLoadedOnce = chatId => state =>
  !!chatId && state.teamChat.hasMore[chatId] !== undefined;

export const selectTeamChatCursor = chatId => state => state.teamChat.cursors[chatId] ?? null;

export const selectTeamChatTypingUsers = chatId => state =>
  chatId ? (state.teamChat.typingUsers[chatId] ?? EMPTY_TYPING) : EMPTY_TYPING;

export const selectTeamChatActiveChannelId = state => state.teamChat.activeChannelId ?? null;

export const selectPendingEntityChatAttachment = state =>
  state.teamChat.pendingEntityAttachment ?? null;

export const selectPendingEntityChatFilter = state =>
  state.teamChat.pendingEntityChatFilter ?? null;

export const selectTeamChatOnlineUsers = chatId => state =>
  chatId ? (state.teamChat.onlineUsers[chatId] ?? EMPTY_ONLINE_USERS) : EMPTY_ONLINE_USERS;

export const selectTeamChatReadReceipts = chatId => state =>
  chatId ? (state.teamChat.readReceipts[chatId] ?? EMPTY_READ_RECEIPTS) : EMPTY_READ_RECEIPTS;

const EMPTY_PINNED_IDS = [];

export const selectTeamChatPinnedMessageIds = chatId => state =>
  chatId ? (state.teamChat.pinnedMessageIds[chatId] ?? EMPTY_PINNED_IDS) : EMPTY_PINNED_IDS;

const selectTeamChatUserMentions = state => state.teamChat.userMentions ?? EMPTY_MENTIONS;

export const selectTeamChatIsLoadingUserMentions = state => !!state.teamChat.isLoadingUserMentions;

export const makeSelectTeamChatUserMentionsWithChannels = () =>
  createSelector([selectTeamChatUserMentions, selectTeamChatChannels], (mentions, channels) => {
    if (!mentions.length) {
      return EMPTY_MENTIONS;
    }

    return mentions.map(mention => {
      const chatId = mention?.chatId ?? null;

      return {
        chatId,
        channel: chatId ? (channels[chatId] ?? null) : null,
        message: {
          id: mention?.messageId,
          memberId: mention?.senderMemberId,
          payload: mention?.payload,
          created: mention?.created,
          updated: mention?.updated,
        },
        messageId: mention?.messageId ?? null,
        timestamp: mention?.created ?? null,
      };
    });
  });
